import { country, language } from '@theme/localization-detect';
import { pass } from '@theme/corner-queue';

const FEATURE = [{ name: 'consent-tracking-api', version: '0.1' }];

/** A key that belongs to this installed theme and to no other. */
const scopedKey = (name) => {
  const theme = window.Shopify?.theme?.id;
  return theme ? `atelier:${theme}:${name}` : null;
};

class LocalizationPanel extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-localization-dialog]');
    this.card = this.querySelector('[data-localization-card]');
    this.form = this.querySelector('[data-localization-form]');
    this.key = scopedKey('localization');
    /* A shop shown to other people asks every visit, and once inside one. */
    this.always = this.dataset.askEveryVisit !== undefined;
    if (!this.dialog || !this.form || !this.key) {
      pass('localization-panel');
      return;
    }
    const asked = this.asked();
    if (!asked) {
      this.begin();
      return;
    }
    if (asked === 'visit') {
      pass('localization-panel');
      this.followConsent();
      return;
    }
    /* Kept across visits only while preferences stay allowed: a note left from before they were
       withdrawn is forgotten, and the panel asks again. */
    this.loadPrivacy((api) => {
      if (api?.preferencesProcessingAllowed?.() === false) {
        try {
          window.localStorage.removeItem(this.key);
        } catch {
          // The panel asks all the same.
        }
        this.begin();
        return;
      }
      pass('localization-panel');
      this.followConsent();
    });
  }

  begin() {
    this.lists = Array.from(this.querySelectorAll('[data-choice-kind]'), (list) => this.read(list));
    for (const entry of this.lists) this.field(entry.field).value = this.chosen(entry);
    this.suggestRegion();
    this.suggestLanguage();

    this.addEventListener('click', this.onClick);
    this.form.addEventListener('submit', this.onSubmit);
    this.dialog.addEventListener('close', this.onDialogClose);
    this.addEventListener('keydown', this.onKeydown);

    /* The panel comes first of the corner cards, before the cookie banner. */
    this.loadPrivacy();
    this.open();
  }

  /**
   * What each option shows, taken once, before anything is written: the
   * summary is rewritten from this and never from another option.
   */
  read(list) {
    const options = Array.from(list.querySelectorAll('[data-choice-option]'));
    const shown = new Map(
      options.map((option) => [
        option.value,
        {
          name: option.querySelector('[data-choice-name]')?.textContent.trim() || '',
          flag: option.querySelector('[data-choice-flag]')?.getAttribute('src') || '',
          set: option.querySelector('[data-choice-flag]')?.getAttribute('srcset') || '',
        },
      ])
    );
    return {
      list,
      options,
      shown,
      field: options[0]?.name || '',
      summary: list.querySelector('[data-choice-summary]'),
      details: list.querySelector('[data-choice-details]'),
    };
  }

  chosen(entry) {
    return entry.options.find((option) => option.getAttribute('aria-current') === 'true')?.value || '';
  }

  /** The value travels in a field of the form, not in the button pressed. */
  field(name) {
    let input = this.form.querySelector(`input[type="hidden"][name="${name}"]`);
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      this.form.appendChild(input);
    }
    return input;
  }

  pick(entry, value) {
    const shown = entry.shown.get(value);
    if (!shown) return;

    const name = entry.summary?.querySelector('[data-choice-name]');
    if (name) name.textContent = shown.name;
    const flag = entry.summary?.querySelector('[data-choice-flag]');
    if (flag) {
      flag.hidden = !shown.flag;
      if (shown.flag) {
        flag.setAttribute('src', shown.flag);
        if (shown.set) flag.setAttribute('srcset', shown.set);
      }
    }
    for (const option of entry.options) {
      if (option.value === value) option.setAttribute('aria-current', 'true');
      else option.removeAttribute('aria-current');
    }
    this.field(entry.field).value = value;
    if (entry.details) entry.details.open = false;
  }

  /**
   * Shopify's consent API, for where the note that the panel was answered is
   * kept; the panel does not wait for it.
   */
  loadPrivacy(callback) {
    const shopify = window.Shopify;
    if (typeof shopify?.loadFeatures !== 'function') {
      callback?.(null);
      return;
    }
    shopify.loadFeatures(FEATURE, (error) => callback?.(error ? null : shopify.customerPrivacy));
  }

  /** Shown, not modal: the page behind it stays in use. */
  open() {
    if (this.dialog.open) return;
    this.dialog.show();
    /* On the next frame: an element still being shown cannot take focus. */
    requestAnimationFrame(() => requestAnimationFrame(() => this.card?.focus({ preventScroll: true })));
  }

  /** Where the note that the panel was answered is: 'visit', 'across' (visits), or '' for none. */
  asked() {
    const places = this.always ? ['visit'] : ['visit', 'across'];
    for (const place of places) {
      try {
        const store = place === 'visit' ? window.sessionStorage : window.localStorage;
        if (store.getItem(this.key)) return place;
      } catch {
        // Denied or absent storage answers nothing, and the panel is shown.
      }
    }
    return '';
  }

  /**
   * Where the note lives says how long it lives: remembering a choice across
   * visits is a preference, and preferences are the visitor's to allow. The
   * panel comes before the cookie question, so the note usually starts out
   * for this visit only, and followConsent() moves it once the visitor
   * allows preferences, and back should they withdraw them.
   */
  remember() {
    if (this.always) {
      try {
        window.sessionStorage.setItem(this.key, '1');
      } catch {
        // Nothing to do: the panel comes back, which is the safe way round.
      }
      return;
    }
    const api = window.Shopify?.customerPrivacy;
    const across = typeof api?.preferencesProcessingAllowed === 'function' && api.preferencesProcessingAllowed();
    try {
      (across ? window.localStorage : window.sessionStorage).setItem(this.key, '1');
    } catch {
      // Nothing to do: the panel comes back, which is the safe way round.
    }
  }

  /**
   * The note follows the visitor's consent to preferences while the page is open: it is kept
   * across visits once preferences are allowed, and goes back to this visit only once they are
   * withdrawn, so a visitor who turns off remembering their choices is asked again on the next
   * visit. It used to move one way only: a note kept across visits stayed after the consent was
   * withdrawn, and the panel never came back.
   */
  followConsent() {
    if (this.always) return;
    const place = () => {
      const allowed = window.Shopify?.customerPrivacy?.preferencesProcessingAllowed?.();
      if (typeof allowed !== 'boolean') return;
      try {
        const from = allowed ? window.sessionStorage : window.localStorage;
        const to = allowed ? window.localStorage : window.sessionStorage;
        if (!from.getItem(this.key)) return;
        to.setItem(this.key, '1');
        from.removeItem(this.key);
      } catch {
        // Denied storage: the note stays where it is.
      }
    };
    if (!this.followingConsent) {
      this.followingConsent = true;
      document.addEventListener('visitorConsentCollected', place);
    }
    this.loadPrivacy(place);
  }

  onClick = (event) => {
    if (event.target.closest('[data-localization-close]')) {
      this.dialog.close();
      return;
    }
    const option = event.target.closest('[data-choice-option]');
    if (!option) return;
    const entry = this.lists.find((one) => one.options.includes(option));
    if (!entry) return;
    event.preventDefault();
    entry.touched = true;
    if (option.hasAttribute('data-choice-detect')) {
      this.pickDetected(entry, option);
      return;
    }
    this.pick(entry, option.value);
  };

  /**
   * The region list starts on the country the platform places the visitor in
   * by address, when the store sells there; otherwise on the one the
   * storefront is in. Shopify does not switch the storefront by address on
   * its own, so without this the panel would offer the shop's default
   * country. A country of the market shown as one row is offered as that row.
   * A choice the visitor has already made, or a list they have opened, is
   * left alone.
   */
  async suggestRegion() {
    const entry = this.lists.find((one) => one.list.dataset.choiceKind === 'region');
    if (!entry) return;
    const code = await country();
    if (!code || entry.touched || entry.details?.open) return;
    if (entry.shown.has(code)) {
      this.pick(entry, code);
      return;
    }
    const group = entry.options.find((option) => option.hasAttribute('data-choice-detect'));
    if (!group) return;
    this.pick(entry, group.value);
    this.field(entry.field).value = code;
  }

  /**
   * The language list starts on the language the platform suggests from the
   * browser, when it is published; otherwise on the one the storefront is in.
   * A choice the visitor has already made is left alone.
   */
  async suggestLanguage() {
    const entry = this.lists.find((one) => one.list.dataset.choiceKind === 'language');
    if (!entry) return;
    const code = await language();
    if (!code || entry.touched || entry.details?.open || !entry.shown.has(code)) return;
    this.pick(entry, code);
  }

  /** Other countries: the row is shown as chosen, the country posted is the visitor's by address. */
  async pickDetected(entry, option) {
    const code = await country();
    if (!code) {
      if (entry.details) entry.details.open = false;
      return;
    }
    this.pick(entry, option.value);
    this.field(entry.field).value = code;
  }

  /**
   * The form takes the visitor to the same page in the chosen region and
   * language. The panel stays up until that page arrives: closing it here
   * would let the cards behind it in the queue show for the moment the old
   * page is still on screen, and vanish with it.
   */
  onSubmit = () => {
    this.remember();
  };

  /* A non-modal dialog does not answer Esc by itself. */
  onKeydown = (event) => {
    if (event.key !== 'Escape') return;
    if (this.querySelector('[data-choice-details][open]')) return;
    this.dialog.close();
  };

  onDialogClose = () => {
    this.remember();
    this.followConsent();
    pass('localization-panel');
  };
}

if (!customElements.get('localization-panel')) {
  customElements.define('localization-panel', LocalizationPanel);
}
